import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Tabs,
  Tab,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  CircularProgress,
  Typography,
  ListItemButton
} from "@mui/material";

/** 
 * Export this interface so `AppTester` (or anywhere else) can import 
 * and reference GraphPermission objects.
 */
export interface GraphPermission {
  id: string;
  value: string;         // e.g. "Calendars.Read"
  displayName?: string;  // e.g. "Read user calendars"
  description?: string;  // e.g. "Allows reading user calendars."
  /** 
   * We'll store the type ("Scope" for delegated, "Role" for app perms)
   * so we don't need to rely on activeTab outside the component.
   */
  type: "Scope" | "Role"; 
}

interface GraphPermissionsPickerProps {
  open: boolean;                      // Whether the dialog is open
  onClose: () => void;                // Called if user cancels
  onSubmit: (selected: GraphPermission[]) => void; 
  graphAccessToken: string;           // Token with Directory.Read.All has to be added to API permissions and admin consented!!!
}

const GraphPermissionsPicker: React.FC<GraphPermissionsPickerProps> = ({
  open,
  onClose,
  onSubmit,
  graphAccessToken
}) => {
  // We'll store *all* perms in two arrays, each tagged with .type = "Scope" or "Role"
  const [delegatedPerms, setDelegatedPerms] = useState<GraphPermission[]>([]);
  const [appPerms, setAppPerms] = useState<GraphPermission[]>([]);

  // activeTab: 0 = delegated, 1 = application
  const [activeTab, setActiveTab] = useState<number>(0);

  // For search filtering
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Loading / error states
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  // Map from permissionId to boolean indicating it's checked
  const [checkedMap, setCheckedMap] = useState<Record<string, boolean>>({});

  // On open => fetch data
  useEffect(() => {
    if (!open) return;
    // Clear prior state
    setDelegatedPerms([]);
    setAppPerms([]);
    setSearchQuery("");
    setCheckedMap({});
    setError("");
    setLoading(true);

    fetchPermissions().finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Actually fetch the Graph SP
  const fetchPermissions = async () => {
    try {
      const url =
        "https://graph.microsoft.com/v1.0/servicePrincipals" +
        "?$filter=appId eq '00000003-0000-0000-c000-000000000000'" +
        "&$select=appRoles,oauth2PermissionScopes";

      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${graphAccessToken}` },
      });
      if (!resp.ok) {
        throw new Error(`Fetch Graph SP failed: ${resp.status} ${resp.statusText}`);
      }

      const json = await resp.json();
      if (!json.value || !json.value.length) {
        throw new Error("Microsoft Graph service principal not found in this tenant.");
      }
      const graphSP = json.value[0];

      // Build arrays
      const delegatedRaw = (graphSP.oauth2PermissionScopes || []).filter((p: any) => p.isEnabled);
      const delegatedList: GraphPermission[] = delegatedRaw.map((p: any) => ({
        id: p.id,
        value: p.value,
        displayName: p.adminConsentDisplayName,
        description: p.adminConsentDescription,
        type: "Scope"
      }));

      const appRaw = (graphSP.appRoles || []).filter((r: any) => r.isEnabled);
      const appList: GraphPermission[] = appRaw.map((r: any) => ({
        id: r.id,
        value: r.value,
        displayName: r.displayName,
        description: r.description,
        type: "Role"
      }));

      setDelegatedPerms(delegatedList);
      setAppPerms(appList);
    } catch (err: any) {
      console.error("Error fetching Graph perms:", err);
      setError(err.message || "Unknown error");
    }
  };

  // Filter whichever list is active
  const getDisplayedList = (): GraphPermission[] => {
    const baseList = activeTab === 0 ? delegatedPerms : appPerms;
    if (!searchQuery.trim()) return baseList;

    const lower = searchQuery.toLowerCase();
    return baseList.filter(
      (p) =>
        p.value.toLowerCase().includes(lower) ||
        (p.displayName || "").toLowerCase().includes(lower) ||
        (p.description || "").toLowerCase().includes(lower)
    );
  };

  // Toggle check
  const handleCheck = (permId: string) => {
    setCheckedMap((prev) => ({
      ...prev,
      [permId]: !prev[permId],
    }));
  };

  // Switch tabs
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setSearchQuery("");
  };

  // On OK => gather selected perms, pass them upward,
  // then alert & close
  const handleSubmit = () => {
    const allPerms = [...delegatedPerms, ...appPerms];
    const selected = allPerms.filter((p) => checkedMap[p.id]);

    onSubmit(selected);

    // Display the same “Click Apply Changes…” message here:
    alert("Click Apply Changes to update API permissions!");
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Pick Microsoft Graph Permissions</DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ textAlign: "center", my: 3 }}>
            <CircularProgress />
            <Typography>Loading permissions...</Typography>
          </Box>
        ) : error ? (
          <Typography color="error">Error: {error}</Typography>
        ) : (
          <>
            <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
              <Tab label="Delegated Permissions" />
              <Tab label="Application Permissions" />
            </Tabs>

            <TextField
              label="Search"
              variant="outlined"
              fullWidth
              sx={{ mb: 2 }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <List dense sx={{ maxHeight: 400, overflow: "auto", border: "1px solid #ccc" }}>
              {getDisplayedList().map((perm) => (
                <ListItem key={perm.id} disablePadding>
                  <ListItemButton onClick={() => handleCheck(perm.id)}>
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={!!checkedMap[perm.id]}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={perm.value}
                      secondary={perm.description || perm.displayName || ""}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>

    <DialogActions>
      <Button
        onClick={() => {
        alert("Click Apply Changes to update API permissions!");
        onClose();
        }}
      >
        Close
      </Button>
      <Button variant="contained" onClick={handleSubmit}>
        OK
      </Button>
    </DialogActions>
    </Dialog>
  );
};

export default GraphPermissionsPicker;
